// Catalog.ID Client — Shared Email + Phone Verification Steps
// ============================================================================
// ONE implementation of the two claim steps every registration flow runs
// (registration-restructure §13.3, M13). Personal registration has had them
// since M1; round 6 makes them mandatory for delegates as well — the founding
// delegate during organization registration, and every invited delegate during
// activation — because a validated email AND phone claim is exactly what the
// Account Unlock Sheet is sealed against.
//
// The sequence per claim (§4.3) is:
//
//   encryptClaim(value, attr_encrypt PUBLIC)      — the node never sees plaintext
//   POST /api/claims                              — the claim row
//   getValidatorKey()                             — the validator's KEM key
//   reEncryptClaimKey(…, attr_encrypt SECRET, …)  — hand the key to the validator
//   POST /api/claims/{uuid}/validate              — email link / SMS code
//
// and the wizard then waits for the certificate (email: poll the claim, the
// link is clicked in another tab; phone: type the code).
//
// PARAMETERISATION. Everything account-specific arrives through
// {@link ClaimStepsConfig} as accessors, evaluated at click time rather than
// captured at construction: the wizards build their steps BEFORE the account
// exists. The two keys are the important ones — the org wizard's claims belong
// to the founding DELEGATE (its session, its attr key), never to the
// organization, and the activation wizard's belong to the invited delegate.
//
// STATE. The step state lives in the wizard's `shared` map under the same field
// names the personal wizard has always used (`emailClaimUuid`, `phoneVerified`,
// …), so all three wizards share one resume-stash shape and the print step
// finds the claim uuids where it expects them.
//
// FIXED VALUES (§13.5, amended by §14.2). An invitation may FIX the email
// and/or the phone. The field is then EMPTY AND EDITABLE — the member types the
// address printed on their card — and only a note says so; M18 removed the
// prefill because the plaintext no longer travels in the link (one invitation
// must behave the same way however the invitee arrived). `emailFixed` /
// `phoneFixed` turn the note on.
//
// Enforcement is not here and never was: the node holds only a salted digest,
// and the VALIDATOR compares. Because it compares before sending any mail or
// SMS, a mistyped fixed value is refused immediately and the retry is clean —
// which is why an empty editable field is safe copy, and why the refusal has to
// arrive as exactly the sentence the note used ({@link FIXED_VALUE_MISMATCH_MESSAGE}).
//
// The SALT that digest was built with never reaches the node (§14.2): it comes
// from the invitation code, on this device, and `valueSalt()` hands it here to
// be sealed to the validator's KEM key and sent alongside the claim key.

import { createElement } from '@catalog-shell';
import type { WizardStep } from '@catalog-shell';
import { encryptClaim, reEncryptClaimKey, wrapSymmetricKey } from '../crypto/claims';
import * as claimsApi from '../api/claims';
import * as aes from '../crypto/aes';

export type ClaimKind = 'email' | 'phone';

export interface ClaimStepsConfig {
    /** The wizard's shared state — also what its resume stash persists. */
    shared: Record<string, unknown>;
    /** Persist `shared` (the wizard's own stash writer). */
    stashSync: () => void;
    /** attr_encrypt PUBLIC key (base64) of the account the claims belong to. */
    attrPublicKey: () => string;
    /** attr_encrypt PRIVATE key (base64) of that same account. */
    attrPrivateKey: () => string;
    /** True when the account's invitation FIXES that channel (§14.2). Only a note
     *  changes — the field stays empty and editable; the validator enforces. */
    emailFixed?: () => boolean;
    phoneFixed?: () => boolean;
    /** The fixed-value salt derived from the invitation code, 32 lowercase hex.
     *  Sealed to the validator's KEM key and sent with the validation request;
     *  never sent to the node in any readable form. */
    valueSalt?: () => string | undefined;
    /** Copy overrides. Defaults are the personal wizard's wording verbatim. */
    copy?: ClaimStepsCopy;
}

export interface ClaimStepsCopy {
    emailHeading?: string;
    emailIntro?: string;
    phoneHeading?: string;
    phoneIntro?: string;
}

/** Every network/crypto call the sequence makes, injectable for tests. */
export interface ClaimApiDeps {
    encryptClaim: typeof encryptClaim;
    createClaim: typeof claimsApi.createClaim;
    getValidatorKey: typeof claimsApi.getValidatorKey;
    reEncryptClaimKey: typeof reEncryptClaimKey;
    wrapSymmetricKey: typeof wrapSymmetricKey;
    validateClaim: typeof claimsApi.validateClaim;
    getClaim: typeof claimsApi.getClaim;
    verifyPhone: typeof claimsApi.verifyPhone;
}

export const defaultClaimDeps: ClaimApiDeps = {
    encryptClaim,
    createClaim: claimsApi.createClaim,
    getValidatorKey: claimsApi.getValidatorKey,
    reEncryptClaimKey,
    wrapSymmetricKey,
    validateClaim: claimsApi.validateClaim,
    getClaim: claimsApi.getClaim,
    verifyPhone: claimsApi.verifyPhone,
};

// ── the `shared` field names, in one place ──────────────────────────────────

const FIELDS = {
    email: {
        value: 'emailAddress',
        uuid: 'emailClaimUuid',
        encKey: 'emailClaimEncKey',
        sent: 'emailValidationSent',
        done: 'emailValidated',
        saltEnv: 'emailClaimSaltEnvelope',
    },
    phone: {
        value: 'phoneNumber',
        uuid: 'phoneClaimUuid',
        encKey: 'phoneClaimEncKey',
        sent: 'phoneValidationSent',
        done: 'phoneVerified',
        saltEnv: 'phoneClaimSaltEnvelope',
    },
} as const;

const PHONE_TOKEN_FIELD = 'phoneVerificationToken';

// ── DOM-free core ───────────────────────────────────────────────────────────

/**
 * Create the claim and ask the validator to validate it. Writes the resume-safe
 * fields into `shared` and stash-syncs ONCE, only after every call succeeded —
 * a half-written state would make the resume path believe a claim exists that
 * the node never got.
 */
export async function submitClaimForValidation(
    kind: ClaimKind,
    rawValue: string,
    cfg: ClaimStepsConfig,
    deps: ClaimApiDeps = defaultClaimDeps,
): Promise<void> {
    const value = rawValue.trim();
    const f = FIELDS[kind];

    const encrypted = await deps.encryptClaim(value, aes.fromBase64(cfg.attrPublicKey()));
    const claim = await deps.createClaim({
        claimType: kind,
        encryptedValue: encrypted.encryptedValue,
        nonce: encrypted.nonce,
        encryptedKey: encrypted.encryptedKey,
    });

    // Re-encrypt the claim key for the validator (§4.3) — with the attr SECRET
    // key of the account these claims belong to.
    const { publicKey: validatorKeyB64 } = await deps.getValidatorKey();
    const validatorKey = aes.fromBase64(validatorKeyB64);
    const validatorEncryptedKey = await deps.reEncryptClaimKey(
        encrypted.encryptedKey,
        aes.fromBase64(cfg.attrPrivateKey()),
        validatorKey,
    );
    // The fixed-value salt (§14.2), sealed to the validator in the SAME envelope
    // the claim key uses — the node forwards it unopened and can read neither.
    const saltEnvelope = await sealValueSalt(cfg, validatorKey, deps);
    const resp = await deps.validateClaim(claim.claimUuid, {
        validationType: kind, encryptedKey: validatorEncryptedKey,
        ...(saltEnvelope ? { encryptedValueSalt: saltEnvelope } : {}),
    });

    cfg.shared[f.value] = value;
    cfg.shared[f.uuid] = claim.claimUuid;
    cfg.shared[f.encKey] = validatorEncryptedKey;
    if (saltEnvelope) cfg.shared[f.saltEnv] = saltEnvelope;
    cfg.shared[f.sent] = true;
    if (kind === 'phone') cfg.shared[PHONE_TOKEN_FIELD] = resp.verificationToken;
    cfg.stashSync();
}

/**
 * Seal the fixed-value salt to the validator's KEM key, or null when this
 * account has none (the ordinary, unconstrained case). Sealed with
 * `wrapSymmetricKey` — a generic hybrid-KEM seal, not a key-shaped one, and the
 * exact envelope the validator's `decrypt_key` opens.
 */
async function sealValueSalt(
    cfg: ClaimStepsConfig, validatorKey: Uint8Array, deps: ClaimApiDeps,
): Promise<string | null> {
    const salt = cfg.valueSalt?.();
    if (!salt) return null;
    // The salt's 32 ASCII hex characters, because they are what the digest was
    // built over — the bytes they encode would hash to something else entirely.
    return aes.toBase64(await deps.wrapSymmetricKey(aes.encodeUtf8(salt), validatorKey));
}

/** Re-send the validation request for a claim that already exists. The sealed
 *  salt is reused from the stash rather than re-sealed: the validator key has
 *  not moved, and re-sealing would mean another round trip for nothing. */
export async function resendClaimValidation(
    kind: ClaimKind, cfg: ClaimStepsConfig, deps: ClaimApiDeps = defaultClaimDeps,
): Promise<void> {
    const f = FIELDS[kind];
    const saltEnvelope = cfg.shared[f.saltEnv];
    const resp = await deps.validateClaim(cfg.shared[f.uuid] as string, {
        validationType: kind, encryptedKey: cfg.shared[f.encKey] as string,
        ...(typeof saltEnvelope === 'string' ? { encryptedValueSalt: saltEnvelope } : {}),
    });
    if (kind === 'phone') {
        cfg.shared[PHONE_TOKEN_FIELD] = resp.verificationToken;
        cfg.stashSync();
    }
}

/**
 * Has the certificate landed? Answers from the flag when it is already set (a
 * resume, or a second call), otherwise asks the node. Flips the flag and
 * stash-syncs on the transition, and writes nothing otherwise. Throws only when
 * the lookup itself failed — callers distinguish "not yet" from "cannot tell".
 */
export async function refreshClaimValidation(
    kind: ClaimKind, cfg: ClaimStepsConfig, deps: ClaimApiDeps = defaultClaimDeps,
): Promise<boolean> {
    const f = FIELDS[kind];
    if (cfg.shared[f.done] === true) return true;
    const claim = await deps.getClaim(cfg.shared[f.uuid] as string);
    if (claim.validationStatus !== 'validated') return false;
    cfg.shared[f.done] = true;
    cfg.stashSync();
    return true;
}

/** Submit the SMS code. True marks the phone verified (and stash-syncs). */
export async function confirmPhoneCode(
    code: string, cfg: ClaimStepsConfig, deps: ClaimApiDeps = defaultClaimDeps,
): Promise<boolean> {
    const result = await deps.verifyPhone(cfg.shared[PHONE_TOKEN_FIELD] as string, code.trim());
    if (!result.success) return false;
    cfg.shared[FIELDS.phone.done] = true;
    cfg.stashSync();
    return true;
}

/**
 * What the input field starts as, and whether the invitation fixes it.
 *
 * `fixed` does NOT lock the field (§14.2): the plaintext is on the card, not in
 * the link, so there is nothing to prefill and the member types it. What `fixed`
 * changes is the note above the field — and, if they get it wrong, the validator
 * refuses before any mail is sent and they simply try again.
 *
 * A value already in the stash (a resume, or a second visit to the step) still
 * comes back, fixed or not.
 */
export function claimFieldState(
    kind: ClaimKind, cfg: ClaimStepsConfig,
): { value: string; fixed: boolean } {
    const fixed = (kind === 'email' ? cfg.emailFixed : cfg.phoneFixed)?.() === true;
    const stored = cfg.shared[FIELDS[kind].value];
    return { value: typeof stored === 'string' ? stored : '', fixed };
}

// ── the two wizard steps ────────────────────────────────────────────────────

const DEFAULT_COPY: Required<ClaimStepsCopy> = {
    emailHeading: 'Validate Your Email',
    emailIntro: 'Add an email claim and verify it. Registration completes after both your '
        + 'email and phone are verified.',
    phoneHeading: 'Validate Your Phone',
    phoneIntro: 'Add a phone claim and verify it with the code we text you.',
};

// What each channel is FOR, in words a member has at this step: the Account
// Unlock Sheet and the printed document set it refers to are only introduced
// at the print step (staging test 2026-10-15).
const EMAIL_NOTE = 'A verified email address is required. If you ever lose your password, a code sent to '
    + 'this address is part of how you get back into your account. The address is also printed on '
    + 'your documents at the end of registration.';
const PHONE_NOTE = 'A verified phone number is required. If you ever lose your password, a code sent to '
    + 'this number by text message is part of how you get back into your account. The number is also '
    + 'printed on your documents at the end of registration.';

/** Email verification. Mandatory: Next is gated on the certificate, because
 *  the account only activates once both certs exist and the Unlock Sheet is
 *  sealed against them. */
export function emailVerificationStep(cfg: ClaimStepsConfig): WizardStep {
    const copy = { ...DEFAULT_COPY, ...cfg.copy };
    return {
        id: 'email',
        label: 'Email',
        hideBack: true,
        async validate() {
            if (!cfg.shared[FIELDS.email.sent]) return 'Add and verify an email address to continue';
            if (cfg.shared[FIELDS.email.done]) return true;
            try {
                if (!await refreshClaimValidation('email', cfg)) {
                    return 'Your email is not verified yet — click the link in the verification email, then press Next.';
                }
            } catch {
                return 'Could not check verification status — try again in a moment';
            }
            return true;
        },
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: copy.emailHeading }));
            step.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: copy.emailIntro }));

            // The claim is mandatory, so Next stays disabled — saying why — until
            // it is verified. The link is clicked in another tab, where the member
            // may still be, so the poll below only ENABLES Next; it never moves on.
            if (!cfg.shared[FIELDS.email.done]) {
                ctx.wizard.setNextEnabled(false, cfg.shared[FIELDS.email.sent]
                    ? 'Waiting for you to click the link in the verification email'
                    : 'Send a verification email and click the link in it first');
            }

            if (cfg.shared[FIELDS.email.sent]) {
                const statusEl = createElement('p', {
                    className: 'cid-verify-success',
                    textContent: cfg.shared[FIELDS.email.done]
                        ? 'Email verified — press Next to continue.'
                        : `Verification email sent to ${cfg.shared[FIELDS.email.value]}. Click the link in it — `
                            + 'Next unlocks as soon as you have.',
                });
                step.appendChild(statusEl);

                const resendBtn = createElement('button', {
                    className: 'cs-btn cs-btn--default',
                    textContent: 'Resend verification email',
                });
                resendBtn.addEventListener('click', async () => {
                    resendBtn.setAttribute('disabled', '');
                    resendBtn.textContent = 'Sending...';
                    try {
                        await resendClaimValidation('email', cfg);
                        resendBtn.textContent = 'Sent!';
                    } catch {
                        resendBtn.textContent = 'Resend verification email';
                        resendBtn.removeAttribute('disabled');
                    }
                });
                if (!cfg.shared[FIELDS.email.done]) {
                    step.appendChild(resendBtn);

                    // Poll until the verification lands (link is clicked in another
                    // tab), then flip the UI and open Next. Stops when the step is
                    // left — the wizard keeps its content element, so that element
                    // being connected says nothing; this step's own does.
                    const poll = setInterval(async () => {
                        if (!step.isConnected) { clearInterval(poll); return; }
                        try {
                            if (await refreshClaimValidation('email', cfg)) {
                                clearInterval(poll);
                                statusEl.textContent = 'Email verified — press Next to continue.';
                                resendBtn.remove();
                                ctx.wizard.setNextEnabled(true);
                            }
                        } catch { /* transient — keep polling */ }
                    }, 3000);
                    ctx.disposables.add(() => clearInterval(poll));
                }

                container.appendChild(step);
                return;
            }

            const field = claimFieldState('email', cfg);
            const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
            const input = document.createElement('input');
            input.type = 'email';
            input.className = 'cid-auth-input';
            input.placeholder = 'Email address';
            input.value = field.value;
            fieldWrapper.appendChild(input);
            step.appendChild(fieldWrapper);
            if (field.fixed) {
                step.appendChild(createElement('p', {
                    className: 'cid-pane-note',
                    textContent: claimsApi.FIXED_VALUE_MISMATCH_MESSAGE,
                }));
            }

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            step.appendChild(errorEl);

            const sendBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: 'Send verification email',
            });

            const self = this;
            sendBtn.addEventListener('click', async () => {
                const email = input.value.trim();
                if (!email || !email.includes('@')) {
                    errorEl.textContent = 'Enter a valid email address';
                    errorEl.classList.add('cid-pane-error--visible');
                    return;
                }

                errorEl.classList.remove('cid-pane-error--visible');
                sendBtn.setAttribute('disabled', '');
                sendBtn.textContent = 'Creating claim...';

                try {
                    await submitClaimForValidation('email', email, cfg);
                    container.innerHTML = '';
                    self.render(container, ctx);
                } catch (err) {
                    sendBtn.removeAttribute('disabled');
                    sendBtn.textContent = 'Send verification email';
                    // A fixed-value refusal must reach the member as exactly the
                    // sentence the note above the field used (§14.2) — not as a
                    // generic "failed to send".
                    errorEl.textContent = claimsApi.claimValidationErrorMessage(err)
                        ?? (err instanceof Error ? err.message : 'Failed to send verification email');
                    errorEl.classList.add('cid-pane-error--visible');
                }
            });
            step.appendChild(sendBtn);

            step.appendChild(createElement('p', { className: 'cid-pane-note', textContent: EMAIL_NOTE }));

            container.appendChild(step);
        },
    };
}

/** Phone verification. Same mandatory gate; the code is typed here rather than
 *  clicked in another tab, but the claim may also be validated elsewhere. */
export function phoneVerificationStep(cfg: ClaimStepsConfig): WizardStep {
    const copy = { ...DEFAULT_COPY, ...cfg.copy };
    return {
        id: 'phone',
        label: 'Phone',
        hideBack: true,
        async validate() {
            if (cfg.shared[FIELDS.phone.done]) return true;
            if (!cfg.shared[FIELDS.phone.sent]) return 'Add and verify a phone number to continue';
            // The code may have been verified elsewhere (e.g. another tab) — check the claim.
            try {
                if (await refreshClaimValidation('phone', cfg)) return true;
            } catch { /* fall through to the gate message */ }
            return 'Enter the verification code sent to your phone to continue';
        },
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: copy.phoneHeading }));
            step.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: copy.phoneIntro }));

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            const self = this;
            const rerender = (): void => {
                container.innerHTML = '';
                self.render(container, ctx);
            };

            // Mandatory, like the email: Next stays disabled, saying why, until the
            // code is accepted — and then the step moves on by itself (below).
            if (!cfg.shared[FIELDS.phone.done]) {
                ctx.wizard.setNextEnabled(false, cfg.shared[FIELDS.phone.sent]
                    ? 'Enter the code sent to your phone first'
                    : 'Send a verification code to your phone and enter it first');
            }

            if (cfg.shared[FIELDS.phone.done]) {
                step.appendChild(createElement('p', {
                    className: 'cid-verify-success',
                    textContent: 'Phone verified — press Next to continue.',
                }));
                container.appendChild(step);
                return;
            }

            if (cfg.shared[FIELDS.phone.sent]) {
                step.appendChild(createElement('p', {
                    className: 'cid-verify-success',
                    textContent: `Verification code sent to ${cfg.shared[FIELDS.phone.value]}.`,
                }));

                const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
                const codeInput = document.createElement('input');
                codeInput.type = 'text';
                codeInput.inputMode = 'numeric';
                codeInput.autocomplete = 'one-time-code';
                codeInput.className = 'cid-auth-input';
                codeInput.placeholder = 'Verification code';
                fieldWrapper.appendChild(codeInput);
                step.appendChild(fieldWrapper);
                step.appendChild(errorEl);

                const verifyBtn = createElement('button', {
                    className: 'cs-btn cs-btn--yes cid-auth-submit',
                    textContent: 'Verify code',
                });
                verifyBtn.addEventListener('click', async () => {
                    const code = codeInput.value.trim();
                    if (!code) {
                        errorEl.textContent = 'Enter the code from the text message';
                        errorEl.classList.add('cid-pane-error--visible');
                        return;
                    }
                    errorEl.classList.remove('cid-pane-error--visible');
                    verifyBtn.setAttribute('disabled', '');
                    verifyBtn.textContent = 'Verifying...';
                    try {
                        if (await confirmPhoneCode(code, cfg)) {
                            // The member has just typed the code: pressing Next as
                            // well would be a click for nothing.
                            void ctx.wizard.next();
                            return;
                        }
                        errorEl.textContent = 'Invalid code — check the text message and try again';
                        errorEl.classList.add('cid-pane-error--visible');
                    } catch (err) {
                        errorEl.textContent = err instanceof Error ? err.message : 'Verification failed';
                        errorEl.classList.add('cid-pane-error--visible');
                    }
                    verifyBtn.removeAttribute('disabled');
                    verifyBtn.textContent = 'Verify code';
                });
                step.appendChild(verifyBtn);

                const resendBtn = createElement('button', {
                    className: 'cs-btn cs-btn--default',
                    textContent: 'Resend code',
                });
                resendBtn.addEventListener('click', async () => {
                    resendBtn.setAttribute('disabled', '');
                    resendBtn.textContent = 'Sending...';
                    try {
                        await resendClaimValidation('phone', cfg);
                        resendBtn.textContent = 'Sent!';
                    } catch (err) {
                        resendBtn.textContent = 'Resend code';
                        resendBtn.removeAttribute('disabled');
                        // Surface why a resend failed (e.g. the number's country
                        // became unsupported) instead of silently doing nothing.
                        errorEl.textContent = claimsApi.claimValidationErrorMessage(err)
                            ?? (err instanceof Error ? err.message : 'Could not resend the code');
                        errorEl.classList.add('cid-pane-error--visible');
                    }
                });
                step.appendChild(resendBtn);

                container.appendChild(step);
                return;
            }

            const field = claimFieldState('phone', cfg);
            const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
            const input = document.createElement('input');
            input.type = 'tel';
            input.className = 'cid-auth-input';
            input.placeholder = 'Phone number (e.g. +31612345678)';
            input.value = field.value;
            fieldWrapper.appendChild(input);
            step.appendChild(fieldWrapper);
            if (field.fixed) {
                step.appendChild(createElement('p', {
                    className: 'cid-pane-note',
                    textContent: claimsApi.FIXED_VALUE_MISMATCH_MESSAGE,
                }));
            }
            step.appendChild(errorEl);

            const sendBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: 'Send verification code',
            });
            sendBtn.addEventListener('click', async () => {
                const phone = input.value.trim();
                if (!/^\+?[0-9][0-9\s\-()]{5,}$/.test(phone)) {
                    errorEl.textContent = 'Enter a valid phone number, including the country code';
                    errorEl.classList.add('cid-pane-error--visible');
                    return;
                }

                errorEl.classList.remove('cid-pane-error--visible');
                sendBtn.setAttribute('disabled', '');
                sendBtn.textContent = 'Creating claim...';

                try {
                    await submitClaimForValidation('phone', phone, cfg);
                    rerender();
                } catch (err) {
                    sendBtn.removeAttribute('disabled');
                    sendBtn.textContent = 'Send verification code';
                    // Prefer the actionable, provider-classified message (invalid
                    // number, unsupported country, provider down) when the server
                    // relayed a reason; otherwise a generic fallback.
                    errorEl.textContent = claimsApi.claimValidationErrorMessage(err)
                        ?? (err instanceof Error ? err.message : 'Failed to send verification code');
                    errorEl.classList.add('cid-pane-error--visible');
                }
            });
            step.appendChild(sendBtn);

            step.appendChild(createElement('p', { className: 'cid-pane-note', textContent: PHONE_NOTE }));

            container.appendChild(step);
        },
    };
}
